import { CheckCircle } from '@phosphor-icons/react'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTheme } from 'styled-components'

import AlertLocal from 'Components/AlertLocal'
import { Button } from 'DesignSystem'
import LoadingDots from 'DesignSystem/LoadingDots'
import useIntlShort from 'Hooks/useIntlShort.hook'
import useOAuthConsentRequest from 'Hooks/API/useOAuthConsentRequest.hook'
import useQuery from 'Hooks/useQuery.hook'
import { apiPOST } from 'Services/Api.service'
import { addAlert } from 'Store/Actions/alerts.action'

import { RootS, CardS, ScopesS } from './OAuthConsent.styled'

/**
 * Tela de consentimento OAuth.
 *
 * Vem do redirect do /oauth/authorize do user-api, que guarda a solicitação no
 * Redis e manda para cá com o `request_id`.
 *
 * A tela é registrada nos DOIS switches de rota (público e privado): eles são
 * mutuamente exclusivos em Routes/index.tsx, então só no público ela ficaria
 * inalcançável para quem já está logado.
 *
 * Deslogado ela renderiza normalmente — o endpoint que a alimenta é anônimo de
 * propósito — e só manda para o login quando o usuário decide. Assim ele vê o
 * que está autorizando antes de digitar a senha.
 */

const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '::1', '[::1]']

const OAuthConsentPage = () => {
  const intl = useIntlShort()
  const theme = useTheme()
  const dispatch = useDispatch()
  const urlQuery = useQuery()

  const requestId = urlQuery.get('request_id')
  const { data, isLoading, isError } = useOAuthConsentRequest(requestId)

  const [deciding, setDeciding] = useState<'approve' | 'deny' | null>(null)

  const isLogged = !!localStorage.getItem('odash-app:user')

  const goToLogin = () => {
    // pathname + search, encodado: o guard padrão do app descarta a query, e
    // sem o request_id a tela não tem o que exibir na volta
    const next = `${window.location.pathname}${window.location.search}`

    window.location.href = `/login?next=${encodeURIComponent(next)}`
  }

  const decide = async (approve: boolean) => {
    if (!isLogged) {
      goToLogin()
      return
    }

    setDeciding(approve ? 'approve' : 'deny')

    try {
      const response = await apiPOST<any>('oauth/authorize/decision', {
        request_id: requestId,
        approve
      })

      // 4xx não lança do axios (validateStatus: status < 500), então o erro
      // chega como objeto na resposta
      if (response.status === 'error' || !response.redirect_url) {
        dispatch(addAlert({ title: response.message ?? intl('Não foi possível concluir a autorização.'), type: 'error' }))
        setDeciding(null)
        return
      }

      // replace, e não href: o consent não deve ficar no histórico do browser
      window.location.replace(response.redirect_url)
    } catch (error: any) {
      dispatch(addAlert({ title: error?.message ?? intl('Não foi possível concluir a autorização.'), type: 'error' }))
      setDeciding(null)
    }
  }

  if (isLoading) {
    return (
      <RootS>
        <CardS><LoadingDots /></CardS>
      </RootS>
    )
  }

  // solicitação inexistente ou expirada (o TTL é de 10 minutos). Não dá para
  // recuperar daqui: o fluxo tem que recomeçar no Claude.
  if (isError || !data || !requestId) {
    return (
      <RootS>
        <CardS>
          <h3 className="fw-bold text-center">{intl('Solicitação de autorização expirada')}</h3>
          <p className="body1 text-center mt-3">
            {intl('Esta solicitação não existe mais ou expirou. Volte ao aplicativo e tente conectar novamente.')}
          </p>
        </CardS>
      </RootS>
    )
  }

  const { client, resource, scopes } = data
  const appName = client.client_name ?? client.redirect_host ?? intl('Aplicativo')
  const isLoopback = !!client.redirect_host &&
    LOOPBACK_HOSTS.some(host => client.redirect_host?.split(':')[0] === host)

  return (
    <RootS>
      <CardS>
        <img src="/images/isotype.png" width={40} alt={resource.name} />

        <h3 className="fw-bold text-center mt-3">
          {intl('Conecte-se ao {resource}', { resource: resource.name })}
        </h3>
        <p className="body2 text-gray_hover text-center">
          {intl('Autorize {app} a acessar o {resource}', { app: appName, resource: resource.name })}
        </p>

        {/**
          * `client_name` é escolhido por quem registra o aplicativo, então
          * "verificado" só vale quando alguém da empresa aprovou. Enquanto isso
          * não acontece, o usuário precisa saber.
          */}
        {!client.name_verified && (
          <AlertLocal
            className="mt-3"
            message={intl('Este aplicativo não foi verificado pela {resource}. Confirme que você confia em quem está pedindo o acesso.', { resource: resource.name })}
          />
        )}

        {isLoopback && (
          <AlertLocal
            className="mt-3"
            message={intl('O acesso será devolvido para um programa em execução no seu computador. Só continue se foi você quem iniciou este pedido.')}
          />
        )}

        <ScopesS className="mt-4">
          <li className="body2 fw-bold d-block mb-3">
            {intl('Através do {resource}, {app} poderá:', { resource: resource.name, app: appName })}
          </li>
          {scopes.map(scope => (
            <li key={scope.id}>
              <CheckCircle weight="fill" size={24} color={theme.colors.success} />
              <span className="body2">{scope.description ?? scope.name}</span>
            </li>
          ))}
        </ScopesS>

        {/**
          * O host de retorno é o único dado que o usuário consegue conferir de
          * fato — a spec do MCP exige exibi-lo.
          */}
        {client.redirect_host && (
          <p className="caption1 text-gray_hover text-center mt-3">
            {intl('O acesso será devolvido para {host}', { host: client.redirect_host })}
          </p>
        )}

        <div className="d-flex flex-row gap-3 w-100 mt-4">
          <Button
            className="flex-grow-1"
            color="gray"
            isLoading={deciding === 'deny'}
            disabled={deciding !== null}
            onClick={() => decide(false)}
          >
            {intl('Cancelar')}
          </Button>
          <Button
            className="flex-grow-1"
            color="primary"
            isLoading={deciding === 'approve'}
            disabled={deciding !== null}
            onClick={() => decide(true)}
          >
            {isLogged ? intl('Concordar e autorizar acesso') : intl('Entrar e autorizar')}
          </Button>
        </div>
      </CardS>
    </RootS>
  )
}

export default OAuthConsentPage
